/**
 * Functions for fetching episode details from the Peacock API
 */

import { EpisodeNode } from "./types";

import { fetchAssetDetails } from "@/utils/FetchAssetDetails";
import { isSeason } from "@/utils/FetchAssetDetails/types";

/**
 * Fetches episode details for a season from the Peacock API
 *
 * @remarks
 * Uses the represent parameter to fetch nested episode data from the season node.
 * Episodes are returned in the relationships.items.data array, sorted by episode number.
 *
 * @param seasonId - The season node ID
 * @returns Promise that resolves to an array of episode nodes with full details
 *
 * @example
 * ```typescript
 * // Fetch seasons first
 * const seasons = await fetchSeasonDetails(providerSeriesId);
 *
 * // Then fetch episodes for a specific season
 * fetchEpisodeDetails(seasons[0].id)
 *   .then(episodes => console.log(`Found ${episodes.length} episodes`));
 * ```
 *
 * @throws Will throw an error if the network request fails or no episodes are found
 */
const fetchEpisodeDetails = async (
  seasonId: string,
): Promise<EpisodeNode[]> => {
  try {
    // Use fetchAssetDetails to get the season with nested episode data
    const data = await fetchAssetDetails(seasonId, {
      represent: "(items(items))",
      contentSegments: "*",
      features: "upcoming",
    });

    // Verify we got a season response with episodes
    if (!isSeason(data)) {
      throw new Error("Expected season response but got different type");
    }

    if (!data.relationships?.items?.data) {
      throw new Error("No episodes found in response");
    }

    const episodes = data.relationships.items.data;

    // Sort by episode number
    episodes.sort(
      (a, b) => a.attributes.episodeNumber - b.attributes.episodeNumber,
    );

    return episodes;
  } catch (error) {
    console.error("Error fetching episodes:", error);
    throw error;
  }
};

export { fetchEpisodeDetails };
